import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Card, { CardBody, CardHeader } from '../common/Card';
import { Button } from '../common/Button';
import GradientText from '../common/GradientText';
import VoiceInput from '../common/VoiceInput';
import { useReadingsStore } from '../../store/readingsStore';
import { SpreadType } from '../../types';

const spreadContent = {
  initial: {
    title: 'Test Reading',
    description: 'Begin your test reading by choosing a spread layout. Each spread offers different insights and perspectives.'
  },
  '1-card': {
    title: 'Single Card Reading',
    description: 'A simple but powerful reading focusing on a single card to provide insight into your question.'
  },
  '3-card': {
    title: 'Three Card Reading',
    description: 'A versatile spread that can represent past-present-future, mind-body-spirit, or situation-action-outcome.'
  },
  '5-card': {
    title: 'Five Card Reading',
    description: 'A more complex reading that explores a situation or question you have while considering what other influences may be at play.'
  },
  'celtic-cross': {
    title: 'Celtic Cross Reading',
    description: 'A comprehensive ten-card spread for deep insight into complex situations.'
  },
  'two-options': {
    title: 'Two Options Reading',
    description: 'A specialized spread to help you choose between two different paths or decisions.'
  },
  'relationship': {
    title: 'Relationship Reading',
    description: 'A focused spread exploring the dynamics, challenges, and potential of a relationship.'
  }
};

const TestReading: React.FC = () => {
  const navigate = useNavigate();
  const { setCurrentReading } = useReadingsStore();
  const [step, setStep] = useState<'choose' | 'question' | 'cards'>('choose');
  const [spreadType, setSpreadType] = useState<SpreadType>('1-card');
  const [subject, setSubject] = useState('');
  const [question, setQuestion] = useState('');
  const [context, setContext] = useState('');
  const [cards, setCards] = useState<Array<{ name: string; position: string; isReversed: boolean }>>([]);
  const [userInterpretation, setUserInterpretation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const content = step === 'cards' 
    ? spreadContent[spreadType] || spreadContent.initial
    : spreadContent.initial;

  const handleSpreadTypeSelect = (type: SpreadType) => {
    setSpreadType(type);
    setStep('question');
  };

  const handleQuestionSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (subject && question && context) {
      const positions = getSpreadPositions(spreadType);
      setCards(positions.map(position => ({
        name: '',
        position,
        isReversed: false
      })));
      setStep('cards');
    }
  };

  const handleCardChange = (index: number, value: string) => {
    const updatedCards = [...cards];
    updatedCards[index] = {
      ...updatedCards[index],
      name: value
    };
    setCards(updatedCards);
  };

  const handleReversalToggle = (index: number) => {
    const updatedCards = [...cards];
    updatedCards[index] = {
      ...updatedCards[index],
      isReversed: !updatedCards[index].isReversed
    };
    setCards(updatedCards);
  };

  const handleSubmit = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    setError(null);

    try {
      // Validate all required fields
      if (!subject || !context || !spreadType || !cards.every(card => card.name)) {
        throw new Error('Please fill in all required fields');
      }

      // Set the current reading in the store with test flag
      await setCurrentReading({
        subject,
        question,
        context,
        spreadType,
        cards,
        userInterpretation,
        is_virtual: false,
        readingTitle: 'Test Reading', // Add explicit title
        notes: 'This is a test reading', // Add notes to indicate test
        is_test: true // Add test flag
      });

      // Navigate to interpretation view
      navigate('/interpretation');
    } catch (error) {
      console.error('Error submitting reading:', error);
      setError(error instanceof Error ? error.message : 'An error occurred');
      setIsLoading(false);
    }
  };

  const getSpreadPositions = (type: SpreadType): string[] => {
    switch (type) {
      case '1-card':
        return ['Single Card'];
      case '3-card':
        return ['Past', 'Present', 'Future'];
      case '5-card':
        return ['Center', 'Above', 'Below', 'Left', 'Right'];
      case 'celtic-cross':
        return [
          'Present', 'Challenge', 'Past', 'Future',
          'Above', 'Below', 'Advice', 'Environment',
          'Hopes/Fears', 'Outcome'
        ];
      case 'virtual-daily-card':
        return ['Daily Card'];
      case 'two-options':
        return [
          'What Grounds You', 'Your Key Motivation', 'Your Ideal Outcome', 'About Option 1', 
          'Option 1 Outcome', 'About Option 2', 'Option 2 Outcome'
        ];
      case 'relationship':
        return [
          'You', 'Partner', 'Relationship Dynamic', 'Challenges',
          'Strengths', 'Potential', 'Advice'
        ];
      default:
        return ['Single Card'];
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="max-w-4xl mx-auto"
    >
      <Card>
        <CardHeader>
          <h1 className="text-3xl md:text-4xl font-display font-bold">
            <GradientText>{content.title}</GradientText>
          </h1>
          <p className="text-lg text-ink dark:text-parchment/60">
            {content.description}
          </p>
        </CardHeader>

        <CardBody className="p-6">
          {error && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-3 rounded-md flex items-center gap-2">
              <span>{error}</span>
            </div>
          )}

          {step === 'choose' && (
            <div className="text-center space-y-6">
              <div className="bg-parchment dark:bg-shadow/20 p-6 rounded-lg border border-vellum dark:border-dusk">
                <h2 className="text-2xl font-display font-bold text-sun dark:text-ember mb-4">
                  Choose Your Spread
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto mb-6">
                  <button
                    onClick={() => handleSpreadTypeSelect('1-card')}
                    className="p-6 bg-vellum dark:bg-shadow rounded-lg border-2 border-mist dark:border-dusk hover:border-moon dark:hover:border-moon transition-colors text-left"
                  >
                    <h3 className="text-lg font-medium text-sun dark:text-ember mb-2">
                      Single Card
                    </h3>
                    <p className="text-sm text-ink/70 dark:text-parchment/60">
                      Draw a single card for guidance and insight.
                    </p>
                  </button>

                  <button
                    onClick={() => handleSpreadTypeSelect('3-card')}
                    className="p-6 bg-vellum dark:bg-shadow rounded-lg border-2 border-mist dark:border-dusk hover:border-moon dark:hover:border-moon transition-colors text-left"
                  >
                    <h3 className="text-lg font-medium text-sun dark:text-ember mb-2">
                      Three Card Spread
                    </h3>
                    <p className="text-sm text-ink/70 dark:text-parchment/60">
                      One of the most common spreads to get more in depth guidance for a question or situation you have in mind.
                    </p>
                  </button>

                  <button
                    onClick={() => handleSpreadTypeSelect('5-card')}
                    className="p-6 bg-vellum dark:bg-shadow rounded-lg border-2 border-mist dark:border-dusk hover:border-moon dark:hover:border-moon transition-colors text-left"
                  >
                    <h3 className="text-lg font-medium text-sun dark:text-ember mb-2">
                      Five Card Spread
                    </h3>
                    <p className="text-sm text-ink/70 dark:text-parchment/60">
                      A more complex reading that explores a situation or question you have while considering what other influences may be at play.
                    </p>
                  </button>

                  <button
                    onClick={() => handleSpreadTypeSelect('celtic-cross')}
                    className="p-6 bg-vellum dark:bg-shadow rounded-lg border-2 border-mist dark:border-dusk hover:border-moon dark:hover:border-moon transition-colors text-left"
                  >
                    <h3 className="text-lg font-medium text-sun dark:text-ember mb-2">
                      Celtic Cross
                    </h3>
                    <p className="text-sm text-ink/70 dark:text-parchment/60">
                      A comprehensive ten-card spread for deep insight into complex situations.
                    </p>
                  </button>

                  <button
                    onClick={() => handleSpreadTypeSelect('two-options')}
                    className="p-6 bg-vellum dark:bg-shadow rounded-lg border-2 border-mist dark:border-dusk hover:border-moon dark:hover:border-moon transition-colors text-left"
                  >
                    <h3 className="text-lg font-medium text-sun dark:text-ember mb-2">
                      Two Options
                    </h3>
                    <p className="text-sm text-ink/70 dark:text-parchment/60">
                      Compare two different paths or decisions to help you choose the best option.
                    </p>
                  </button>

                  <button
                    onClick={() => handleSpreadTypeSelect('relationship')}
                    className="p-6 bg-vellum dark:bg-shadow rounded-lg border-2 border-mist dark:border-dusk hover:border-moon dark:hover:border-moon transition-colors text-left"
                  >
                    <h3 className="text-lg font-medium text-sun dark:text-ember mb-2">
                      Relationship
                    </h3>
                    <p className="text-sm text-ink/70 dark:text-parchment/60">
                      Explore the dynamics, challenges, and potential of a relationship.
                    </p>
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 'question' && (
            <form onSubmit={handleQuestionSubmit} className="max-w-2xl mx-auto">
              <div className="bg-parchment dark:bg-shadow/20 p-6 rounded-lg border border-vellum dark:border-dusk">
                <h2 className="text-2xl font-display font-bold text-sun dark:text-ember mb-4">
                  What Would You Like to Explore?
                </h2>
                <p className="text-ink dark:text-parchment/60 mb-6">
                  Take a moment to formulate your question. The clearer your intention, the more meaningful your reading will be.
                </p>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-ink dark:text-parchment/60 mb-1">
                      Who is this reading for?
                    </label>
                    <input
                      type="text"
                      id="subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="w-full px-3 py-2 border border-mist dark:border-dusk rounded-md focus:outline-none focus:ring-2 focus:ring-moon bg-vellum dark:bg-shadow"
                      placeholder="e.g., Me, Friend, Client"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="question" className="block text-sm font-medium text-ink dark:text-parchment/60 mb-1">
                      Your Question
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="question"
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        className="w-full px-3 py-2 pr-12 border border-mist dark:border-dusk rounded-md focus:outline-none focus:ring-2 focus:ring-moon bg-vellum dark:bg-shadow"
                        placeholder="What would you like guidance about?"
                        required
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <VoiceInput
                          onTranscript={(text) => setQuestion(prev => prev + (prev ? ' ' : '') + text)}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="context" className="block text-sm font-medium text-ink dark:text-parchment/60 mb-1">
                      Context
                    </label>
                    <div className="relative">
                      <textarea
                        id="context"
                        value={context}
                        onChange={(e) => setContext(e.target.value)}
                        className="w-full px-3 py-2 pr-12 border border-mist dark:border-dusk rounded-md focus:outline-none focus:ring-2 focus:ring-moon bg-vellum dark:bg-shadow"
                        rows={3}
                        placeholder="Provide some context about your question or situation"
                        required
                      />
                      <div className="absolute right-2 top-2">
                        <VoiceInput
                          onTranscript={(text) => setContext(prev => prev + (prev ? ' ' : '') + text)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <Button
                      variant="outline"
                      onClick={() => setStep('choose')}
                    >
                      Back
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={!subject || !question || !context}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          )}

          {step === 'cards' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-medium text-ink dark:text-parchment mb-4">Enter Your Cards</h2>
                <div className="space-y-4">
                  {cards.map((card, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className="flex-grow">
                        <label className="block text-sm font-medium text-ink dark:text-parchment/60 mb-1">
                          {card.position}
                        </label>
                        <input
                          type="text"
                          value={card.name}
                          onChange={(e) => handleCardChange(index, e.target.value)}
                          className="w-full px-3 py-2 pr-12 border border-mist dark:border-dusk rounded-md focus:outline-none focus:ring-2 focus:ring-moon bg-vellum dark:bg-shadow"
                          placeholder="Enter card name"
                        />
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                          <VoiceInput
                            onTranscript={(text) => handleCardChange(index, text)}
                          />
                        </div>
                      </div>
                      <label className="inline-flex items-center cursor-pointer mt-6">
                        <input
                          type="checkbox"
                          checked={card.isReversed}
                          onChange={() => handleReversalToggle(index)}
                          className="sr-only peer"
                        />
                        <div className="relative w-11 h-6 bg-mist peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-mist dark:peer-focus:ring-dusk rounded-full peer dark:bg-dusk peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-vellum after:border-mist after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-dusk peer-checked:bg-sun"></div>
                        <span className="ms-2 text-sm font-medium text-ink/50 dark:text-parchment/60">Reversed</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="interpretation" className="block text-sm font-medium text-ink dark:text-parchment/60 mb-1">
                  What do you see in the cards?
                </label>
                <div className="relative">
                  <textarea
                    id="interpretation"
                    value={userInterpretation}
                    onChange={(e) => setUserInterpretation(e.target.value)}
                    className="w-full px-3 py-2 pr-12 border border-mist dark:border-dusk rounded-md focus:outline-none focus:ring-2 focus:ring-moon bg-vellum dark:bg-shadow"
                    rows={4}
                    placeholder="Share your thoughts on what the cards are telling you"
                  />
                  <div className="absolute right-2 top-2">
                    <VoiceInput
                      onTranscript={(text) => setUserInterpretation(prev => prev + (prev ? ' ' : '') + text)}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => setStep('question')}
                  disabled={isLoading}
                >
                  Back
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  isLoading={isLoading}
                  disabled={!cards.every(card => card.name) || isLoading}
                >
                  Get Interpretation
                </Button>
              </div>
            </div>
          )}

          <div className="mt-8 text-center space-x-4">
            <Link 
              to="/guide/101" 
              className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
            >
              Tarot 101
            </Link>
            <span className="text-mist">•</span>
            <Link 
              to="/center" 
              className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
            >
              Tarot Center
            </Link>
            <span className="text-mist">•</span>
            <Link 
              to="/faq" 
              className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
            >
              FAQ
            </Link>
          </div>
        </CardBody>
      </Card>
    </motion.div>
  );
};

export default TestReading;